<template>
    <div
      :class="cn({
        'container-xl mx-auto': enableContainer,
      }, 'tw:my-4 tw:print:hidden', props.class)"
    >
      <template v-if="withExport || $slots['header-title'] || $slots['header-actions']">
        <div class="tw:flex tw:flex-col tw:md:flex-row tw:gap-4 tw:md:items-center tw:justify-between tw:mb-4">
          <div>
            <slot name="header-title" />
          </div>
          <div class="tw:flex tw:flex-col-reverse tw:md:flex-row tw:items-start tw:md:items-center tw:gap-2">
            <template v-if="withExport && (models.data && models.data.length > 0)">
              <ExportColumnsDialog
                v-if="withColumnPicker"
                v-model="exportColumns"
                :export-url="exportUrl"
                :columns-url="exportColumnsUrl"
              />
              <Button v-else variant="outline" as-child>
                <a :href="exportUrl" target="_blank">
                  <UploadIcon class="tw:w-3 tw:h-3" /> {{ $t('Export to Excel format') }}
                </a>
              </Button>
            </template>

            <slot name="header-actions"
                  :export-url="exportUrl"
                  :export-columns-url="exportColumnsUrl"
                  :get-results="getResults"
            />
          </div>
        </div>
      </template>

      <template v-if="withSearch">
        <slot name="searchForm"
              :search="search"
              :update-search="(value) => search = value"
              :show-scanner="() => showScanner = true"
        >
          <SearchForm
            v-model="search"
            :placeholder="searchPlaceholder"
            :autofocus="autofocusSearch"
          >
            <template v-if="withQrCodeReader" v-slot:input-group-append>
              <button class="tw:flex tw:shrink-0 tw:items-center tw:gap-1.5 tw:px-3 tw:py-2 tw:rounded-r-md tw:hover:bg-zinc-50 tw:outline-1 tw:-outline-offset-1 tw:outline-gray-300 tw:focus:relative tw:focus:outline-2 tw:focus:-outline-offset-2 tw:focus:outline-blue-600 tw:text-sm tw:font-medium" type="button"
                      v-on:click.prevent="showScanner = true">
                <ScanQrCodeIcon class="tw:w-4 tw:h-4 tw:text-zinc-400" /> Scan
              </button>
            </template>
          </SearchForm>
        </slot>
      </template>

      <div v-if="availableFilters.length > 0 || (sortOptions && sortOptions.length > 0)" class="tw:flex tw:justify-between tw:items-top tw:gap-2 py-3 tw:flex-1 tw:max-w-full tw:flex-wrap">
        <template v-if="availableFilters.length > 0">
          <Filters
            v-model="filters"
            :base-filters="baseFilters"
            :available-filters="availableFilters"
          />
        </template>
        <div v-if="sortOptions && sortOptions.length > 0">
          <div class="input-group input-group-sm justify-content-end">
            <select v-model="sortValue" class="custom-select" id="RegistrationSearchSelectSortBy">
              <!-- Listings declare their sort labels in English, as the keys they are. -->
              <option v-for="option in sortOptions" :key="option.value" :value="option.value">{{ $t(option.label) }}</option>
            </select>
            <div class="input-group-append">
              <button class="btn btn-outline-secondary" @click="changeDirection()" type="button">
                <i :class="'fa fa-sort-alpha-' + sortDirection" aria-hidden="true"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <LoadingOverlay :active="loading">
      <template v-if="!loading && (!models.data || models.data.length === 0)">
        <div
          :class="{
            'container-xl mx-auto': enableContainer,
          }"
        >
          <Empty>
            <template #title>
              {{ $t('No {modelName} found', { modelName: modelNameTranslated.toLowerCase() }) }}
            </template>
            <template #description>
              {{ $t('No {modelName} found matching your search or filters.', { modelName: modelNameTranslated.toLowerCase() }) }}
            </template>
            <template #cta v-if="addUrl">
              <a :href="addUrl" class="btn btn-primary tw:!no-underline">
                {{ $t('Create new {modelName}', { modelName: modelNameTranslated.toLowerCase() }) }}
              </a>
            </template>
          </Empty>
        </div>
      </template>
      <div v-else>
        <slot :models="models.data"
              :meta="models.meta"
              :search="search"
              :get-results="getResults"
        ></slot>
      </div>
    </LoadingOverlay>

    <div
      v-if="!withoutPagination"
      :class="cn(
        'tw:mt-4 tw:mb-2',
        'tw:print:hidden',
        enableContainer ? 'container-xl mx-auto' : '',
        props.class
      )"
    >
      <div :class="cn(
        'tw:flex tw:justify-between tw:md:items-center tw:gap-2',
         withoutLimitChange ? 'tw:flex-row tw:justify-end' : 'tw:flex-col-reverse tw:md:flex-row',
        )">
        <div v-if="!withoutLimitChange" class="tw:flex tw:justify-start tw:items-center">
          <label for="searchable-limit" class="tw:text-nowrap mr-2 tw:text-sm tw:text-zinc-600">{{ $t('Results per page') }}</label>
          <select
            id="searchable-limit"
            v-model="limit"
            class="form-control my-1 mr-sm-2 tw:w-auto"
            @change="getResults()">
            <option v-for="i in [10, 20, 50, 100]">{{ i }}</option>
          </select>
        </div>

        <Pagination
          v-if="models.data"
          :data="models"
          :limit=2
          @pagination-change-page="getResults"
        />
      </div>
    </div>

    <BarcodeScanner :show="showScanner"
                    @scan="search = $event; showScanner = false"
                    @discard="showScanner = false" />
</template>

<script setup lang="ts">
import { ref, shallowRef, computed, watch, onMounted, defineExpose } from "vue";
import type {  HTMLAttributes } from "vue";
import { useDebounceFn } from '@vueuse/core';
import qs from 'qs';
import SearchForm from "./SearchForm.vue";
import ExportColumnsDialog from "./ExportColumnsDialog.vue";
import Filters from "@/components/filters/Filters.vue";
import BarcodeScanner from "../form-components/BarcodeScanner.vue";
import LoadingOverlay from "@/components/ui/LoadingOverlay.vue";
import Empty from "@/components/ui/Empty.vue";
import { useI18n } from 'vue-i18n';
import {
  getModelByName,
  getModelByString,
  ModelName,
  uiDisplayNames,
} from '@/models';
import type { ModelType } from '@/models';
import { useModel } from '@/composables/useModel';
import { Button } from "@/shadcn/components/ui/button";
import Pagination from "@/components/Pagination.vue";
import {
  UploadIcon,
  ScanQrCodeIcon,
} from '@lucide/vue'

import { cn } from "@/shadcn/lib/utils";
import type { SearchableModelProps } from "@/components/searchable-model/index";
const { t } = useI18n();

// Utilisation de defineProps avec le type
const props = withDefaults(
  defineProps<SearchableModelProps & {
    class? : HTMLAttributes["class"],
  }>(),
  {
    searchValue: '',
    availableFilters: () => [],
    baseFilters: () => ({}),
    includes: () => [],
    sortOptions: () => [],
    addUrl: undefined,
    sort: null,
    loadOnStart: true,
    withSearch: true,
    withQrCodeReader: false,
    withExport: false,
    withColumnPicker: false,
    pushState: false,
    enableContainer: false,
    limit: 20,
    withoutLimitChange: false,
    withoutPagination: false,
  }
)

// Helper functions
function getSortValueFromProps() {
  if (!props.sort) {
    return props.sortOptions[0]?.value ?? '';
  } else {
    return props.sort.startsWith('-') ? props.sort.slice(1) : props.sort;
  }
}

function getSortDirectionFromProps() {
  return props.sort?.startsWith('-') ? 'desc' : 'asc';
}

// Reactive data
const search = ref(props.searchValue);
const models = ref({});
const filters = ref({});
const showScanner = ref(false);
const sortValue = ref(getSortValueFromProps());
const sortDirection = ref(getSortDirectionFromProps());
const count = ref(0);
const page = ref(1);
const limit = ref(props.limit);
const loading = ref(false);
const Model = ref<ModelType | null>(null);
const isInitializing = ref(true);
const exportColumns = ref<string[]>([]);

// Held in a ref rather than a plain binding: the export URLs are computed from it and
// are read on the first render, before onMounted assigns it. A plain binding would leave
// them cached empty for good.
const modelComposable = shallowRef<ReturnType<typeof useModel> | null>(null);

// Computed properties
const exportUrl = computed(() => {
  if (!modelComposable.value?.getModelQuery.value) return '';
  return modelComposable.value.getModelQuery.value.exportUrl(exportColumns.value);
});

const exportColumnsUrl = computed(() => {
  if (!modelComposable.value?.getModelQuery.value) return '';
  return modelComposable.value.getModelQuery.value.exportColumnsUrl();
});

// Methods
function queryString(extraQuery = {}) {
  const query = {
    ...Object.entries(filters.value).filter((filter) => {
      return props.availableFilters.findIndex((f) => f.name === filter[0]) > -1;
    }).filter((filter) => filter[1] !== '')
      .reduce((acc, filter) => {
        acc['filter[' + filter[0] + ']'] = filter[1];
        return acc;
      }, {}),
    sort: (sortDirection.value === 'asc' ? '' : '-') + sortValue.value,
    search: search.value,
    page: page.value,
    limit: limit.value,
    ...extraQuery
  };
  return qs.stringify(query);
}

async function getResults(p) {
  const currentPage = parseInt(p || page.value);
  page.value = currentPage;
  loading.value = true;

  if (!modelComposable.value?.executeQuery) {
    loading.value = false;
    return;
  }

  try {
    const response = await modelComposable.value.executeQuery({
      page: currentPage,
      limit: parseInt(limit.value),
      sort: sortValue.value,
      sortDirection: sortDirection.value
    });

    models.value = response;
    count.value = response.meta.total;

    if (props.pushState) {
      window.history.pushState(null, null, window.location.pathname + '?' + queryString());
    }
  } catch (error) {
    console.error('Error fetching results:', error);
  } finally {
    loading.value = false;
  }
}

function parseUrl() {
  const parsed = qs.parse(window.location.search, { ignoreQueryPrefix: true });

  Object.entries(parsed).forEach(([key, value]) => {
    if (key === 'filter') {
      Object.entries(value).forEach(filter => {
        filters.value[filter[0]] = filter[1];
      });
    } else if (key === 'sort') {
      const sortStr = String(value ?? '');
      const isDesc = sortStr.startsWith('-');
      sortValue.value = isDesc ? sortStr.slice(1) : sortStr;
      sortDirection.value = isDesc ? 'desc' : 'asc';
    } else if (key === 'page') {
      page.value = parseInt(value as string) || 1;
    } else if (key === 'search') {
      search.value = value as string;
    } else if (key === 'limit') {
      limit.value = parseInt(value as string) || 20;
    }
  });
}

function applyDefaultFilters() {
  props.availableFilters.forEach(filter => {
    if (filter.default !== undefined && filter.default !== null) {
      // N'applique la valeur par défaut que si le filtre n'a pas déjà une valeur
      if (filters.value[filter.name] === undefined || filters.value[filter.name] === null || filters.value[filter.name] === '') {
        filters.value[filter.name] = filter.default;
      }
    }
  });
}

const debouncedSearch = useDebounceFn(function() {
  getResults();
}, 200);

function refresh() {
  page.value = 1;
  getResults();
}

function changeDirection() {
  sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
}

// Watchers
watch(filters, () => {
  if (!isInitializing.value) {
    page.value = 1; // Reset to first page when filters change
    debouncedSearch();
  }
}, { deep: true });

// Watched by value, not by reference: callers commonly pass an inline object literal, which the
// parent recreates on every render, so a reference watch would refetch on unrelated re-renders.
watch(() => JSON.stringify(props.baseFilters), () => {
  if (!isInitializing.value) {
    page.value = 1; // Reset to first page when base filters change
    debouncedSearch();
  }
});

watch(search, () => {
  if (!isInitializing.value) {
    page.value = 1; // Reset to first page when search changes
    debouncedSearch();
  }
});

watch(sortValue, () => {
  if (!isInitializing.value) {
    debouncedSearch();
  }
});

watch(sortDirection, () => {
  if (!isInitializing.value) {
    debouncedSearch();
  }
});

const modelNameTranslated = ref('N/A');
onMounted(async () => {
  /**
   * if adding a new model, remember to translate the lowercase model name in the i18n files
   * e.g. 'Registration' -> 'registration'
   */
  if (typeof props.modelName === ModelName) {
    Model.value = getModelByName(props.modelName);
  } else {
    Model.value = getModelByString(props.modelName);
  }

  // Initialize the composable after Model is set
  modelComposable.value = useModel(Model.value, {
    // Getter, not a snapshot: base filters can change after mount (e.g. the booking flow
    // narrowing the equipment list to a selected period).
    baseFilters: () => props.baseFilters,
    includes: props.includes,
    search,
    filters
  });

  let modelKey = typeof props.modelName === 'string' ? props.modelName : String(props.modelName);
  if (uiDisplayNames[modelKey]) {
    modelNameTranslated.value = t(uiDisplayNames[modelKey]);
  } else {
    modelNameTranslated.value = t(modelKey);
  }

  // Apply default value
  applyDefaultFilters();

  // Parse URL parameters
  parseUrl();
  
  if (props.loadOnStart) {
    await getResults();
  }

  // Mark initialization as complete AFTER first getResults
  // to prevent watchers from resetting page during init
  isInitializing.value = false;
});
defineExpose({
  getResults,
  refresh,
})
</script>
